import React, { useState, useEffect, useReducer, useContext, useCallback } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
// styles
// assets
// material ui assets
import Skeleton from '@material-ui/lab/Skeleton';
import { ScrollContext } from 'App';
import { closeCreateKRPanelAction, openCreateKRPanelAction } from 'actions/createKRPanelActions';
// components
import { setTeamAction, resetAllAction, setFiltersAction } from 'actions/globalFilters';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import useForceUpdate from 'components/hooks/useForceUpdate';
// project data and models
import TeamModel from 'models/team';
// selectors
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
// import logger from 'use-reducer-logger';
import { useSelectedView } from 'reducers/viewsSlice';
// helpers
import { getStartTSEndTSFromDateFilters } from 'screens/Team/TeamPageFilters/helper';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';

import { TASK_TYPES_2 } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';

import { getStartEndTimeFromDurationKey } from 'services/helpers/task';

import { initialFiltersState, filtersReducersWithClearAll } from '../../../TeamPageFilters';
import buildTeamPageFilters from '../../../TeamPageFilters/buildTeamPageFilters';
import { getSelectedFiltersCountFromMap, dateParser } from '../../../TeamPageFilters/helper';
import TeamTasksCardView from './RabbitTeamTasksCardView';

function useScrollToTopState(pathname) {
  const { current: scrollParentRef } = useContext(ScrollContext);

  useEffect(() => {
    // when you come on team page, make sure that you scroll to the top
    if (scrollParentRef) {
      scrollParentRef.scrollTo(0, 0);
    }
  }, [pathname, scrollParentRef]);
}

export const getLocalStorageFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  const val = localStorage.getItem(key);
  return (val && JSON.parse(val, dateParser)) || null;
};

export const setLocalStorageFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export const getLocalStorageStatsFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  const val = localStorage.getItem(key);

  return (val && JSON.parse(val)) || null;
};

const defaultGoalStats = [
  {
    type: 'STATUS',
    value: 'ON_TRACK',
    displayValue: 'On-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'OFF_TRACK',
    displayValue: 'Off-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'AT_RISK',
    displayValue: 'At-Risk',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'CLOSED',
    displayValue: 'CLOSED',
    operator: 'IN',
  },
];

export const defaultTaskStats = [
  {
    type: 'STATUS',
    value: 'ONGOING',
    displayValue: 'On-going',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'DELAYED',
    displayValue: 'Delayed',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'UNPLANNED',
    displayValue: 'Unplanned',
    operator: 'IN',
  },
];

export const getDefaultLocalStorageStatsFilters = (type) => {
  const val = getLocalStorageStatsFilters(type);
  if (val) {
    return val;
  }

  if (type === TASK_TYPES_2.goal) {
    setLocalStorageStatsFilters(type, defaultGoalStats);
    return defaultGoalStats;
  }
  setLocalStorageStatsFilters(type, defaultTaskStats);
  return defaultTaskStats;
};

export const setLocalStorageStatsFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export function useTeamPageState({ sideNavSelectedCohort }) {
  const location = useLocation();
  // const [performanceStats, setPerformanceStats] = useState({ reliabilityScore: 0, growth: 0 });
  const [team, setTeam] = useState(null);

  const dispatch = useDispatch();
  // const [tasksLoading, updateTasksLoading] = useState(false);
  const activeFilters = useSelector(selectTeamPageGlobalFilters, shallowEqual).filters;
  // will be used in Stats when treeview is selected

  // const [activeFilters, activeFiltersDispatch] = useReducer(filtersReducer, []);

  useScrollToTopState(location.pathname);

  const [newFiltersState, newFiltersDispatch] = useReducer(
    // logger(filtersReducersWithClearAll),
    filtersReducersWithClearAll,
    initialFiltersState
  );

  return {
    // performanceStats,
    // setPerformanceStats,
    team,
    setTeam,
    // updateTasksLoading,
    activeFilters,

    activeFiltersDispatch: dispatch,
    newFiltersState,
  };
}

function getCompletedOnDurationFromFilters(newFiltersState) {
  const selectedCompletedFiltersCount = getSelectedFiltersCountFromMap(newFiltersState.completedOnFilters);
  return selectedCompletedFiltersCount > 0
    ? getStartTSEndTSFromDateFilters(newFiltersState.completedOnFilters)
    : getStartEndTimeFromDurationKey('LASTYEAR');
}

function Card() {
  const location = useLocation();
  const queryString = location.search;

  const dispatch = useDispatch();
  const theme = useTheme();

  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);

  const [timeBoundDetailsOfCreatedObjective, setTimeBoundDetailsOfCreatedObjective] = useState(null);

  const [refreshData, markDataAsStale] = useForceUpdate();

  const closeKRPanel = useCallback(() => {
    setIsOpenCreateGoalPanel(false);
    setTimeBoundDetailsOfCreatedObjective(null);
    dispatch(closeCreateKRPanelAction());
  }, [dispatch]);

  const openKRPanel = useCallback(() => {
    dispatch(
      openCreateKRPanelAction({
        onClose: closeKRPanel,
        onCancel: closeKRPanel,
        onSave: markDataAsStale,
        selectedTimeBoundPeriod: selectedTimelines?.[0] ?? {},
        ownerEmployee: loggedInEmployee,
        subObjective: timeBoundDetailsOfCreatedObjective,
        actionDispatchSource: 'Team Page',
        panelHeaderText: 'Add Key Result',
      })
    );
  }, [
    dispatch,
    closeKRPanel,
    selectedTimelines,
    loggedInEmployee,
    timeBoundDetailsOfCreatedObjective,
    markDataAsStale,
  ]);

  const {
    team,
    setTeam,
    // updateTasksLoading,
    activeFilters,
    activeFiltersDispatch,
    newFiltersState,
    selectedTaskType,
    addStatsFilter,
    removeStatsFilter,
    tasksLoading,
  } = useTeamPageState({ sideNavSelectedCohort });

  const [duration, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));

  // had to keep new filter logic separate from the old, so that it works on mobile as well
  // TODO: remove old filter logic and only use this new one
  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(
    () => () => {
      dispatch(resetAllAction());
    },
    [dispatch]
  );

  /* 
    Following effect open KRPanel is isOpenCreateGoalPanel is true.
    Also it clears newly created objective in case of closing of KRPanel
  */
  useEffect(() => {
    if (isOpenCreateGoalPanel) openKRPanel();
  }, [isOpenCreateGoalPanel, openKRPanel]);

  if (!team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  if (!sideNavSelectedCohort) {
    return <Skeleton height="30vh" />;
  }

  return (
    <Box padding="16px" height="auto" position="relative">
      {isOpenCreateObjectivePanel && (
        <ObjectiveSidePanel
          open={isOpenCreateObjectivePanel}
          onCancel={() => {
            setTimeBoundDetailsOfCreatedObjective(null);
            // setIsGoalPanelOfCreatedObjective(false);
            setIsOpenCreateObjectivePanel(false);
          }}
          selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
          isAlignmentAtParentLevel
          setIsOpenCreateGoalPanel={(value) => {
            setIsOpenCreateGoalPanel(value);
            // setIsGoalPanelOfCreatedObjective(value);
          }}
          setTimeboundDetailsOfCreatedObjective={setTimeBoundDetailsOfCreatedObjective}
          source="Team Page"
        />
      )}
      <Box marginTop="6px" padding="16px">
        <TeamTasksCardView
          tagId={team.id}
          activeFilters={activeFilters}
          refreshData={refreshData}
          // updateTasksLoading={updateTasksLoading}
          selectedTeam={team}
          {...duration}
          timeBoundPeriod={selectedTimelines?.[0] ?? null}
          selectedCohort={sideNavSelectedCohort}
        />
      </Box>
    </Box>
  );
}
export default Card;
